import { createApp } from 'vue'
import App from './App.vue'
import './style.css'
import { eraseLocal, migrateNow, migrationState, openDatabase, openLocalDriver, storageInfo } from './db'
import { clearPendingMove, copyLedger, hasLedger, pendingMove, setPendingMove, tableCounts } from './db/move'
import { MIGRATIONS } from './db/schema'
import { protectStorage } from './db/storage'
import { getBackup, hasBackups, saveBackup } from './db/backup'
import { hydrate } from './stores'
import { startUrlSync } from './stores/url'
import { startClock } from './stores/clock'
import { startAchievements } from './stores/achievements'
import { startShortcuts } from './stores/shortcuts'
import { openHelpOnce } from './stores/help'
import { applyStoredTheme } from './stores/theme'
import { initPWA, newerBuild, reloadToLatest } from './pwa'
import {
  renderAhead,
  renderError,
  renderFatal,
  renderLocked,
  renderMigrating,
  renderMoveFailed,
  renderMoving,
  renderScan,
  renderShowCode,
  renderSignIn,
  renderUnlock,
  bootWords,
  sayError,
} from './boot'
import { preferredLanguage } from './lib/languages'
import { readCode } from './lib/device-codes'
import { canScan, qrSvg, startScanner } from './camera'
import { openSheet, state, toast } from './stores/state'
import { count, startUsage } from './stores/usage'
import * as cloud from './stores/cloud'
import { destroySealed, openSealedDriver, unlock as unlockSealed } from './stores/sealed'
import { currentLedger, driverFile, ensureLedgers, isLegacyFile, removeLedger } from './stores/ledgers'
import { currentState, nextStep, setNextStep } from './stores/ledger-states'
import { dropped, filesToDrop } from './db/drops'
import { direct } from './db'
import { finishMoveToVault, isJoined, markSignedIn, readSyncCode, setVaultOrg, startSync, syncing, vaultFile, vaultOrg } from './stores/sync'
import { uninstall as stopCapture } from 'tenantdb/sync'
import { t } from './stores/i18n'

applyStoredTheme()
const app = createApp(App)

function downloadLatestBackup() {
  hasBackups()
    .then((has) => has && listAndDownload())
    .catch((err) => console.error(err))
}

async function listAndDownload() {
  const { listBackups } = await import('./db/backup')
  const [latest] = await listBackups()
  if (!latest) return
  const record = await getBackup(latest.id)
  const url = URL.createObjectURL(new Blob([record.bytes], { type: 'application/x-sqlite3' }))
  const a = document.createElement('a')
  a.href = url
  a.download = `okane-before-update-${latest.id}.sqlite`
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Boot order: open the database, then decide what has to happen before the app
 * can safely mount.
 *   - database newer than the code  → tell the user to update; do not mount
 *   - migrations pending            → run them, with progress and a backup
 *   - otherwise                     → straight through
 *
 * In cloud mode the database is the org's ledger on the server: signing in
 * comes first, and migrations are the server's — the app only checks that the
 * two versions agree.
 */
async function boot() {
  if (await onNewestBuild()) return
  takeDeviceCodes()
  if (!(await finishMoves())) return
  const ready = cloud.getMode() === 'cloud' ? await bootCloud() : await bootLocal()
  if (!ready) return

  await hydrate()
  // The address bar can only be read once the tags, plans and goals it names
  // are loaded.
  startUrlSync()
  startClock()
  app.mount('#app')
  // A first visit starts with how OKANE works; never again after that, unless asked.
  openHelpOnce()
  initPWA()
  // Anything earned since the last visit is said once the app is up.
  startAchievements()
  // The page's keyboard shortcuts (lib/shortcuts.js), each shown on its button.
  startShortcuts()
  // How it is used: counts, not people (docs/analytics-plan.md).
  startUsage()
  count('open', currentState())
  count('platform', globalThis.matchMedia?.('(pointer: coarse)').matches ? 'phone' : 'desktop')
  count('language', state.settings.language)
  if (typeof __OKANE_VERSION__ === 'string') count('version', __OKANE_VERSION__)
  if (arrived) toast(t(arrived), 'good')
  // A code another device showed, opened here: approve it, signed in; else say why not.
  if (pairing) {
    if (state.cloud.account) openSheet('device', { mode: 'approve', ...pairing })
    else toast(t('device.signInFirst'), 'warn')
  }
  // A ledger that came down from the cloud: its server copy goes now.
  const move = pendingMove()
  if (move?.to === 'local' && cloud.getMode() === 'local') finishMoveToLocal(move)
  // Half way across the square (stores/ledger-states.js): the second step, in Settings.
  const next = nextStep()
  setNextStep(null)
  const nextTo = next && next.through === currentState() ? next.to : undefined
  // A sign-in link asked for from Settings: spend it, and back to where it was asked.
  if (syncLogin) {
    try {
      localStorage.removeItem('okane.syncSignIn')
    } catch {
      // nothing kept, nothing to clear
    }
    cloud
      .verifyLink(syncLogin)
      .then(() => {
        markSignedIn(true)
        openSheet('settings', { tab: 'sync', next: nextTo })
      })
      .catch((err) => toast(err.message, 'warn'))
  } else if (nextTo) openSheet('settings', { tab: 'sync', next: nextTo })
  // An encrypted org: the ledger is on this device, and travels sealed.
  if (syncing()) startSync({ reload: reloadSoon })
  // Files no row names any more (a transaction deleted while the app was closing),
  // and files waiting to go up (an attach offline, a ledger decrypted in place).
  import('./stores/attachments')
    .then((m) => (m.sweep(), m.flushOutbox()))
    .catch(() => {})
  // A waiting device's code, opened here by a camera app: approve it in Settings.
  if (syncApprove) {
    if (syncing()) openSheet('settings', { tab: 'sync', approve: syncApprove })
    else toast(t('sync.approveElsewhere'), 'warn')
  }
}

/**
 * A code in the address bar (a QR code opened by the camera, a sign-in link)
 * is read by whatever build this page is. A cached old one would read it the
 * old way, so when a newer build is waiting it is loaded first — once per
 * address, the code still in it. True when the page is reloading.
 */
async function onNewestBuild() {
  const params = new URLSearchParams(location.search)
  if (!['login', 'org', 'sync', 'syncdevice', 'pair'].some((name) => params.has(name))) return false
  try {
    if (sessionStorage.getItem('okane.codeReload') === location.search) return false
    if (!(await newerBuild())) return false
    sessionStorage.setItem('okane.codeReload', location.search)
  } catch {
    return false
  }
  reloadToLatest()
  return true
}

/**
 * Deletes a ledger file of this device: the first device's (`okane.sqlite`)
 * with every copy kept of it (`eraseLocal`), any other on its own.
 */
async function eraseFile(file) {
  if (isLegacyFile(file)) return eraseLocal(await openLocalDriver())
  const left = await openLocalDriver({ file })
  await left.erase()
}

/**
 * What a move left to finish, before anything opens a database (stores/
 * ledger-states.js records it first, so a move cut short finishes here):
 *   files it left behind      an encrypted org's copy no longer this device's
 *   { to: 'vault', org }      local mode's ledger into a new encrypted org's file
 *   { to: 'sealed' }          the readable file, once the ledger is sealed
 *   { to: 'unsealed' }        the sealed ledger, once it is readable or in a vault
 * The first encrypted sync (2026-09-29) kept its org in `okane.sync` and its
 * ledger in local mode's file: the same move as `vault`.
 */
async function finishMoves() {
  try {
    const legacy = JSON.parse(localStorage.getItem('okane.sync') ?? 'null')
    if (legacy?.org && cloud.getMode() !== 'cloud') setPendingMove({ to: 'vault', org: legacy.org })
    localStorage.removeItem('okane.sync')
  } catch {
    // nothing kept
  }
  try {
    for (const file of filesToDrop()) {
      // `sealed:<blob>|<keys>`: an encrypted ledger deleted here; else a file.
      if (file.startsWith('sealed:')) {
        const [blob, keys] = file.slice(7).split('|')
        await destroySealed({ blob, keys })
      } else if (file.startsWith('files:')) {
        // `files:<org>`: the files this device kept of a ledger no longer here.
        await (await import('./stores/attachments')).dropDeviceFiles(file.slice(6))
      } else await eraseFile(file)
      dropped(file)
    }
    const move = pendingMove()
    if (move?.to === 'vault') await finishMoveToVault(move, { eraseLocal })
    else if (move?.to === 'sealed') await eraseFile(move.file ?? 'okane.sqlite')
    else if (move?.to === 'unsealed') await destroySealed(move.blob ? move : { blob: 'okane.sealed', keys: 'okane.sealed.keys' })
    else return true
  } catch (err) {
    if (err?.code === 'db-locked') renderLocked({ onRetry: () => location.reload() })
    else renderFatal(sayError(preferredLanguage())(err))
    return false
  }
  clearPendingMove()
  return true
}

/**
 * The code is older than the data — almost always a cached bundle, the data
 * having been moved on by a newer build (the server's, in the cloud). Try once,
 * by itself, to reload onto the newest build; the screen stays for when that
 * was not it (a backup restored from a newer OKANE), and its button tries again.
 * Once per pair of versions, so a data file that really is newer cannot loop.
 */
function showAhead(dbVersion, codeVersion) {
  renderAhead({ dbVersion, codeVersion, onReload: () => reloadToLatest() })
  const tried = `${codeVersion}>${dbVersion}`
  let before = null
  try {
    before = sessionStorage.getItem(AHEAD_KEY)
    sessionStorage.setItem(AHEAD_KEY, tried)
  } catch {
    // No storage: the button still works.
  }
  if (before !== tried) reloadToLatest()
}
const AHEAD_KEY = 'okane.aheadTried'

/** A move that finished during boot, said once the app (and its language) is up. */
let arrived = ''

/** A `?pair=` code opened on this device, waiting for the app to approve it. */
let pairing = null

/**
 * The two QR codes are links into the app (lib/device-codes.js), so a phone's
 * camera app can open them as well as OKANE's scanner. Both come out of the
 * address bar before anything else reads it.
 *   ?login=  signs this device in. From local mode that is switching to the
 *            cloud, and switching carries the ledger along, as it always does.
 *   ?pair=   is another device asking to be signed in: approved in the app.
 */
let syncLogin = null
/** A waiting device's code (stores/sync.js), opened here: approve it once the app is up. */
let syncApprove = null
const dropParams = (...names) => {
  const params = new URLSearchParams(location.search)
  for (const name of names) params.delete(name)
  history.replaceState(null, '', location.pathname + (params.size ? `?${params}` : '') + location.hash)
}
const signingInForSync = () => {
  try {
    return localStorage.getItem('okane.syncSignIn') === '1'
  } catch {
    return false
  }
}

function takeDeviceCodes() {
  const own = readSyncCode(location.href)
  if (own?.kind === 'device') {
    syncApprove = own
    dropParams('syncdevice')
    return
  }
  if (own?.kind === 'join') {
    // A device link naming its org: sign in (bootCloud spends ?login=) and open
    // that org, whichever kind. This device's own ledger, if it has one, stays
    // in local mode's file — joining an org is not moving a ledger.
    cloud.setMode('cloud')
    cloud.setOrgId(own.org)
    dropParams('org', 'sync')
    return
  }
  const code = readCode(location.href, location.origin)
  if (code?.kind === 'login' && cloud.getMode() !== 'cloud') {
    if (signingInForSync()) {
      // Signing in to turn sync on: this device stays local, and the ledger with it.
      syncLogin = code.token
      const params = new URLSearchParams(location.search)
      params.delete('login')
      history.replaceState(null, '', location.pathname + (params.size ? `?${params}` : '') + location.hash)
    } else {
      setPendingMove({ to: 'cloud', stage: 'copy' })
      cloud.setMode('cloud')
    }
  }
  if (code?.kind === 'pair') {
    pairing = { id: code.id, secret: code.secret }
    const params = new URLSearchParams(location.search)
    params.delete('pair')
    history.replaceState(null, '', location.pathname + (params.size ? `?${params}` : '') + location.hash)
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Showing a code from the sign-in screen, for a signed-in phone to scan
 * (tenantdb's api/devices.go, direction 2), and waiting for it.
 */
function showPairingCode(language, back) {
  let gone = false
  const leave = () => {
    gone = true
    back()
  }
  const start = async () => {
    let started
    try {
      started = await cloud.startPairing()
    } catch (err) {
      back(sayError(language)(err))
      return
    }
    const svg = await qrSvg(started.url)
    const draw = (step) =>
      renderShowCode({ language, svg, code: started.code, state: step, onBack: leave, onRenew: start })
    draw('waiting')
    while (!gone) {
      await sleep(2000)
      if (gone) return
      try {
        const answer = await cloud.collectPairing(started.id)
        // Approved: the session cookie came with the answer.
        if (answer?.user) return location.reload()
      } catch (err) {
        if (err?.code === 'expired') return draw('expired')
        // A dropped connection: keep asking.
      }
    }
  }
  start()
}

/** Scanning, from the sign-in screen, a code a signed-in device shows (direction 1). */
function scanToSignIn(language, back) {
  let stop = null
  const view = renderScan({
    language,
    onBack: () => {
      stop?.()
      back()
    },
  })
  startScanner(view.video, async (text) => {
    const code = readCode(text, location.origin)
    if (!code) return view.say('device.notOurs', {}, 'bad')
    if (code.kind === 'pair') return view.say('device.signInFirst', {}, 'bad')
    stop?.()
    view.say('device.signingIn')
    try {
      await cloud.verifyLink(code.token)
      location.reload()
    } catch (err) {
      view.tell(sayError(language)(err), 'bad')
    }
  })
    .then((stopper) => (stop = stopper))
    .catch((err) => view.say('device.noCamera', { reason: err?.message ?? String(err) }, 'bad'))
}

/**
 * The second half of moving to this device: the ledger is already here and
 * checked (CloudSheet), so the org on the server is deleted. Kept in the move
 * record until it is — a failure is tried again on the next start.
 */
async function finishMoveToLocal(move) {
  try {
    await cloud.deleteOrg(move.org)
  } catch (err) {
    // Gone already, or no longer ours to delete: nothing left to do.
    if (err?.status !== 404 && err?.status !== 403) {
      console.error('OKANE could not delete the server copy', err)
      toast(t('cloud.serverCopyLeft'), 'warn')
      return
    }
  }
  clearPendingMove()
  toast(t(move.encrypted ? 'sync.encrypted' : 'cloud.movedDown'), 'good')
}

const stayLocal = () => {
  clearPendingMove()
  cloud.setMode('local')
  location.reload()
}

/**
 * The second half of moving to the cloud, once signed in: the device's ledger
 * goes into the org — or into a new one, when the chosen org already holds a
 * ledger or is not ours to overwrite — is checked table by table, and only then
 * deleted here. Returns the org and driver to open, or null when a screen of
 * its own has been drawn.
 */
async function moveToCloud({ account, org, driver, open }) {
  renderMoving({ to: 'cloud' })
  // The ledger going up: the one recorded, else (a move from before ledgers were orgs) the first file.
  const file = pendingMove().ledger?.file ?? 'okane.sqlite'
  let local
  try {
    local = await openLocalDriver({ file: driverFileOf(file) })
  } catch (err) {
    if (err?.code === 'db-locked') {
      renderLocked({ onRetry: () => location.reload() })
      return null
    }
    renderMoveFailed({ message: err?.message ?? String(err), onRetry: () => location.reload(), onStay: stayLocal })
    return null
  }

  let made = null
  if (pendingMove().stage !== 'erase') {
    try {
      const counts = await tableCounts(local)
      // A device with nothing on it has nothing to move — and must not
      // overwrite a ledger already in the cloud.
      if (hasLedger(counts)) {
        const free = driver.role === 'owner' && !hasLedger(await tableCounts(driver, Object.keys(counts)))
        if (!free) {
          made = await cloud.createOrg(account.user.email.split('@')[0] || 'Personal')
          driver.close()
          org = made
          cloud.setOrgId(made.id)
          driver = await open(made)
        }
        await copyLedger({ from: local, to: driver, counts })
        // Its files go up with it, under the org's id, each checked there.
        const files = await import('./stores/attachments')
        const rows = await files.fileRows((sql) => local.all(sql))
        if (rows.length) {
          renderMoving({ to: 'cloud', files: { done: 0, total: rows.length } })
          await files.uploadFiles(pendingMove().ledger?.id ?? currentLedger().id, org.id, rows, (p) => renderMoving({ to: 'cloud', files: p }))
        }
      }
      setPendingMove({ to: 'cloud', stage: 'erase' })
    } catch (err) {
      console.error('OKANE could not move the ledger', err)
      local.close?.()
      // An org made for this move and left half-filled is no one's ledger.
      if (made) await cloud.deleteOrg(made.id).catch(() => {})
      renderMoveFailed({ message: err?.message ?? String(err), onRetry: () => location.reload(), onStay: stayLocal })
      return null
    }
  }

  // The ledger is safe in the cloud from here on. Deleting the device's copy
  // can fail on its own; the record says so, and the next start tries again.
  try {
    if (isLegacyFile(file)) await eraseLocal(local)
    else await local.erase()
    // The files it left on this device go too: they are the server's now.
    const leaving = pendingMove().ledger?.id ?? currentLedger().id
    await (await import('./stores/attachments')).dropDeviceFiles(leaving).catch(() => {})
    if (pendingMove().ledger) removeLedger(pendingMove().ledger.id)
    clearPendingMove()
  } catch (err) {
    console.error('OKANE could not delete the copy on this device', err)
    local.close?.()
  }
  arrived = 'cloud.movedUp'
  return { org, driver }
}

async function bootLocal() {
  // Every ledger is an org (stores/ledgers.js): local mode opens the one chosen here.
  ensureLedgers()
  const ledger = currentLedger()
  if (ledger.kind === 'encrypted') return bootSealed(ledger)
  try {
    // Opening also moves the database out of IndexedDB and onto disk, once.
    await openDatabase({ file: driverFile(ledger) })
  } catch (err) {
    if (err?.code === 'db-locked') renderLocked({ onRetry: () => location.reload() })
    throw err
  }
  // Asked every boot: a browser can grant it later (once the app is installed,
  // say) having refused it before.
  const persisted = await protectStorage()
  Object.assign(state.storage, { ...storageInfo, persisted })

  let migration
  try {
    migration = await migrationState()
  } catch (err) {
    // A database from the first schema format that stopped short of its end.
    renderFatal(sayError(preferredLanguage())(err))
    throw err
  }

  if (migration.ahead) {
    showAhead(migration.dbVersion, migration.codeVersion)
    return false
  }

  if (migration.pending.length) {
    // Show the catch-up screen only for an existing database. A first-ever
    // launch is at version 0 with everything "pending" — that is setup, not a
    // months-behind catch-up, and it runs in milliseconds, so keep it silent.
    await runPending({ showProgress: migration.dbVersion > 0 })
  }
  return true
}

/**
 * A ledger encrypted on this device (stores/sealed.js): the passphrase or the
 * recovery key first, then the ledger opens from memory. No safety copies
 * before a migration — they would be the ledger, readable, on disk.
 */
async function bootSealed(ledger) {
  await new Promise((resolve) =>
    renderUnlock({
      onUnlock: async (secret) => {
        await unlockSealed(ledger, secret)
        resolve()
      },
    }),
  )
  await openDatabase({ driver: await openSealedDriver(ledger) })
  Object.assign(state.storage, { backend: 'sealed', moved: false, persisted: await protectStorage() })
  const migration = await migrationState()
  if (migration.ahead) {
    showAhead(migration.dbVersion, migration.codeVersion)
    return false
  }
  if (migration.pending.length) await runPending({ showProgress: migration.dbVersion > 0, backup: false })
  return true
}

/** Reloads what is on screen when another device changes the ledger. */
let reloading = null
function reloadSoon() {
  clearTimeout(reloading)
  reloading = setTimeout(() => hydrate().catch((err) => console.error(err)), 150)
}

/** A ledger file's name as the driver is opened with it: none for the first device's. */
const driverFileOf = (file) => (isLegacyFile(file) ? undefined : file)

/** Whether this device is moving a ledger of its own up to the cloud. */
async function bringingLedgerUp() {
  const move = pendingMove()
  if (move?.to !== 'cloud' || move.stage === 'erase') return false
  try {
    const local = await openLocalDriver({ file: move.ledger ? driverFileOf(move.ledger.file) : undefined })
    try {
      return hasLedger(await tableCounts(local))
    } finally {
      local.close()
    }
  } catch {
    return true // cannot tell: let the move say what is wrong
  }
}

/**
 * An encrypted org's ledger, from its file on this device. Migrations are this
 * device's to run (capture off: every device runs its own). A file never filled
 * from the vault shows the gate (`SyncGate.vue`) in place of the app until it
 * is: the passphrase, or waiting to be approved.
 */
async function bootVault(org, account = null) {
  try {
    await openDatabase({ file: vaultFile(org.id) })
  } catch (err) {
    if (err?.code === 'db-locked') renderLocked({ onRetry: () => location.reload() })
    throw err
  }
  const persisted = await protectStorage()
  Object.assign(state.storage, { ...storageInfo, persisted })
  const migration = await migrationState()
  if (migration.ahead) {
    showAhead(migration.dbVersion, migration.codeVersion)
    return false
  }
  if (migration.pending.length) {
    await stopCapture(direct)
    await runPending({ showProgress: migration.dbVersion > 0 })
  }
  Object.assign(state.cloud, { account, org, connection: 'local' })
  state.sync.org = org
  state.sync.gate = !(await isJoined())
  return true
}

async function bootCloud() {
  // A sign-in link opens the app with ?login=…; spend it, and take it out of
  // the address bar before anything else reads it.
  const params = new URLSearchParams(location.search)
  const token = params.get('login')
  // An encrypted org open here opens at once, offline too: its ledger is on this
  // device. Who is signed in is asked after (startSync).
  if (!token && syncing()) return bootVault(vaultOrg())
  let message = ''
  if (token) {
    params.delete('login')
    history.replaceState(null, '', location.pathname + (params.size ? `?${params}` : '') + location.hash)
    try {
      await cloud.verifyLink(token)
    } catch (err) {
      message = err.message
    }
  }

  let account
  try {
    account = await cloud.me()
  } catch (err) {
    renderFatal(`${sayError(preferredLanguage())(err)} ${bootWords()('boot.needConnection')}`)
    throw Object.assign(err, { code: err.code ?? 'offline' })
  }
  if (!account) {
    const signIn = (text = message) =>
      renderSignIn({
        message: text,
        passkeys: cloud.passkeysSupported(),
        moving: pendingMove()?.to === 'cloud',
        onLink: (email, language) => cloud.requestLink(email, language),
        onPasskey: async () => {
          await cloud.signInWithPasskey()
          location.reload()
        },
        onLocal: stayLocal,
        onShowCode: (language) => showPairingCode(language, signIn),
        onScan: (language) => scanToSignIn(language, signIn),
        canScan: canScan(),
      })
    signIn(pairing ? t('device.signInFirst') : message)
    return false
  }

  const open = async (org) =>
    createRemoteDriver({
      orgId: org.id,
      base: cloud.apiBase(),
      onChanged: reloadSoon,
      onState: (connection, reason) => {
        state.cloud.connection = connection
        if (connection === 'rolled-back') toast(reason)
        // Signed out, or the ledger is gone (deleted — encrypted, say — or we were
        // taken out of it): boot chooses again what this device can open.
        // Not while this device is the one encrypting it: its recovery key is on screen.
        if (connection === 'signed-out' || (connection === 'gone' && !state.cloud.encrypting)) location.reload()
      },
    })
  const { createDriver: createRemoteDriver } = await import('./db/drivers/remote')
  // In no org, and not bringing a ledger up: there is nothing in the cloud to
  // open — this device's own ledger, rather than a new empty one there.
  if (!(account.orgs ?? []).length && pendingMove()?.to !== 'cloud') {
    cloud.setOrgId(null)
    cloud.setMode('local')
    return bootLocal()
  }
  // A ledger of this device going up becomes the cloud org of its id (stores/ledgers.js).
  const going = pendingMove()?.to === 'cloud' && pendingMove().stage !== 'erase' ? pendingMove().ledger : null
  if (going && !(account.orgs ?? []).some((o) => o.id === going.id)) {
    try {
      account.orgs = [...(account.orgs ?? []), await cloud.createOrg(going.name || t('sync.defaultName'), 'plain', { id: going.id })]
    } catch (err) {
      // Taken (by an org this ledger once was, say): moveToCloud makes a new one.
      if (err?.code !== 'org-exists') throw err
    }
  }
  if (going && (account.orgs ?? []).some((o) => o.id === going.id)) cloud.setOrgId(going.id)
  // One org is one ledger, of one kind: an encrypted org opens from its file
  // here (bootVault), a plain one over the socket.
  let org = await cloud.chooseOrg(account)
  if (org.kind === 'vault') {
    if (!(await bringingLedgerUp())) {
      // This device's own ledger, if any, stays in local mode's file.
      clearPendingMove()
      setVaultOrg(org)
      return bootVault(org, account)
    }
    // Moving this device's ledger up, readable: into a plain org of its own, as ever.
    org = await cloud.createOrg(account.user.email.split('@')[0] || 'Personal')
    cloud.setOrgId(org.id)
  }
  setVaultOrg(null)
  let driver
  try {
    driver = await open(org)
  } catch (err) {
    // The org remembered here is gone, or we were taken out of it: the next one.
    if (err?.code !== 'refused') throw err
    cloud.setOrgId(null)
    org = await cloud.chooseOrg(await cloud.me())
    if (org.kind === 'vault') {
      clearPendingMove()
      setVaultOrg(org)
      return bootVault(org, account)
    }
    driver = await open(org)
  }
  if (pendingMove()?.to === 'cloud') {
    const moved = await moveToCloud({ account, org, driver, open })
    if (!moved) return false
    ;({ org, driver } = moved)
  }
  await openDatabase({ driver })
  Object.assign(state.storage, { backend: 'cloud', moved: false, persisted: null })
  // The server has the last word on what a role may do. A write it refuses — a
  // viewer saving a transaction — or one lost with the connection is said as a
  // message, not left as an error nobody sees.
  // Vue catches what its event handlers throw (a sheet's save), so that is where
  // most refusals arrive; the window listener takes the rest.
  const said = (err) => {
    if (err?.code === 'denied') toast(t('cloud.denied'), 'warn')
    else if (err?.code === 'disconnected') toast(t('cloud.disconnected'), 'warn')
    else return false
    return true
  }
  app.config.errorHandler = (err) => {
    if (!said(err)) console.error(err)
  }
  window.addEventListener('unhandledrejection', (event) => {
    if (said(event.reason)) event.preventDefault()
  })
  Object.assign(state.cloud, { account, org: { ...org, role: driver.role }, connection: 'connected' })

  const codeVersion = MIGRATIONS.length
  if (driver.version > codeVersion) {
    showAhead(driver.version, codeVersion)
    return false
  }
  if (driver.version < codeVersion) {
    renderError({
      message: bootWords()('boot.serverBehind', { server: driver.version, app: codeVersion }),
      canRestore: false,
      onRetry: () => location.reload(),
      onDownload: () => {},
    })
    return false
  }
  return true
}

async function runPending({ showProgress, backup = true }) {
  try {
    await migrateNow({ onProgress: showProgress ? renderMigrating : undefined, backup: backup ? saveBackup : undefined })
  } catch (err) {
    if (err?.code === 'db-ahead') {
      showAhead(err.dbVersion, err.codeVersion)
      throw err
    }
    console.error('OKANE migration failed', err)
    renderError({
      message: err?.message ?? String(err),
      canRestore: await hasBackups().catch(() => false),
      onRetry: () => boot().catch((e) => e?.code || renderFatal(sayError(preferredLanguage())(e))),
      onDownload: downloadLatestBackup,
    })
    throw err
  }
}

boot().catch((err) => {
  // renderError/renderAhead/renderLocked have already drawn their own screens;
  // only an unexpected failure needs the generic fatal fallback.
  if (!err?.code) renderFatal(sayError(preferredLanguage())(err))
  else console.error('OKANE could not start', err)
})
